/**
 * Merge manual/API revenue rows with synthetic rows from confirmed sold orders (Team Sales rules).
 */
import { revenueService } from '../../services/api';
import { adminUserService } from '../../services/adminUserService';
import { fetchAllAdminOrdersForAgg, ADMIN_LIST_PAGE_LIMIT } from './adminOrdersBulkFetch';
import { extractCreatorId, getCreatorDisplayName } from './authUtils';
import { orderCountsTowardSoldSales, normalizeBackendOrderStatus } from './teamSalesOrderEligibility';
import { buildSalesReport, isCodOrder } from './teamSalesReport';
import { storefrontTypeFromApiAccount } from '../../utils/adminPaymentAccountMethod';

function toAmount(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function normalizePm(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/_/g, '-')
    .trim();
}

function normalizeApiTransactions(result) {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  if (result.success && Array.isArray(result.data)) return result.data;
  if (Array.isArray(result.data)) return result.data;
  return [];
}

/** Link API + synthetic rows to the same order (avoid double income). */
export function extractOrderRef(t) {
  if (!t) return null;
  if (t.orderId) return `oid:${String(t.orderId).trim()}`;
  if (t.orderNumber) return `onum:${String(t.orderNumber).trim()}`;
  if (t.order && typeof t.order === 'object') {
    const id = t.order._id || t.order.id;
    if (id) return `oid:${String(id).trim()}`;
    if (t.order.orderNumber) return `onum:${String(t.order.orderNumber).trim()}`;
  }
  if (t.order) return `oid:${String(t.order).trim()}`;
  const tid = String(t.transactionId || t.reference || '').trim();
  const ordMatch = tid.match(/^ORD[-_\s]?(.+)$/i);
  if (ordMatch) return `onum:${ordMatch[1].trim()}`;
  const meta = t.metadata || t.meta || t.orderMeta;
  if (meta && typeof meta === 'object') {
    if (meta.orderId) return `oid:${String(meta.orderId).trim()}`;
    if (meta.orderNumber) return `onum:${String(meta.orderNumber).trim()}`;
  }
  return null;
}

function normalizeApiTransaction(row) {
  const paymentAccount =
    row.paymentAccount && typeof row.paymentAccount === 'object'
      ? row.paymentAccount
      : row.paymentAccount
        ? { _id: row.paymentAccount, name: row.accountName }
        : null;

  const orderRef = extractOrderRef(row);

  return {
    ...row,
    paymentAccount,
    teamMember:
      row.teamMember ||
      row.teamMemberName ||
      row.soldBy ||
      (row.createdBy && typeof row.createdBy === 'object'
        ? row.createdBy.name || row.createdBy.email
        : null) ||
      '—',
    isOrderSourced: Boolean(
      row.isOrderSourced || orderRef || row.orderId || row.orderNumber || row.order
    ),
    orderId: row.orderId || (orderRef?.startsWith('oid:') ? orderRef.slice(4) : undefined),
    orderNumber:
      row.orderNumber || (orderRef?.startsWith('onum:') ? orderRef.slice(5) : undefined),
  };
}

async function fetchTeamUserMap() {
  const map = new Map();
  try {
    for (let page = 1; page <= 50; page += 1) {
      let res = await adminUserService.getUsers({
        role: 'team',
        limit: ADMIN_LIST_PAGE_LIMIT,
        page,
      });
      let list = res?.success && Array.isArray(res.data) ? res.data : [];
      if (list.length === 0 && page === 1) {
        res = await adminUserService.getUsers({ limit: ADMIN_LIST_PAGE_LIMIT, page: 1 });
        const all = res?.success && Array.isArray(res.data) ? res.data : [];
        list = all.filter((u) => String(u?.role || '').toLowerCase() === 'team');
        for (const u of list) {
          const id = u?._id || u?.id;
          if (id) map.set(String(id), u);
        }
        break;
      }
      for (const u of list) {
        const id = u?._id || u?.id;
        if (id) map.set(String(id), u);
      }
      if (list.length < ADMIN_LIST_PAGE_LIMIT) break;
    }
  } catch (e) {
    console.warn('orderRevenueTransactions: team users', e);
  }
  return map;
}

function resolvePaymentAccountFromOrder(order, accounts = []) {
  const directId =
    order.paymentAccount?._id ||
    order.paymentAccount?.id ||
    order.paymentAccount ||
    order.paymentAccountId ||
    order.selectedPaymentAccount;

  if (directId && accounts.length) {
    const idStr = String(directId).trim();
    const hit = accounts.find((a) => String(a._id || a.id).trim() === idStr);
    if (hit) return hit;
  }

  if (order.paymentAccount && typeof order.paymentAccount === 'object' && order.paymentAccount.name) {
    return order.paymentAccount;
  }

  if (isCodOrder(order)) return null;

  const orderPm = normalizePm(order.paymentMethod);
  if (!orderPm) return null;

  let best = null;
  let bestScore = 0;

  for (const account of accounts) {
    if ((account.paymentMethod || '').toLowerCase() === 'investment') continue;
    const accPm = normalizePm(account.paymentMethod);
    const storefront = normalizePm(storefrontTypeFromApiAccount(account));
    const accName = String(account.name || '').toLowerCase();

    let score = 0;
    if (accPm && orderPm === accPm) score = 100;
    else if (storefront && orderPm === storefront) score = 90;
    else if (accPm && (orderPm.includes(accPm) || accPm.includes(orderPm))) score = 70;
    else if (accName && orderPm.includes(accName)) score = 60;
    else if (accPm === 'digital-wallet' && accName.includes('jazz') && orderPm.includes('jazz')) score = 55;
    else if (accPm === 'digital-wallet' && accName.includes('easy') && orderPm.includes('easy')) score = 55;

    if (score > bestScore) {
      bestScore = score;
      best = account;
    }
  }

  return best;
}

function orderRevenueStatus(order) {
  if (isCodOrder(order)) {
    const s = normalizeBackendOrderStatus(order);
    if (s === 'delivered' || s === 'complete' || s === 'completed') return 'completed';
    return 'pending';
  }
  return orderCountsTowardSoldSales(order) ? 'completed' : 'pending';
}

function getOrderTeamMemberLabel(order, teamUserMap) {
  const names = new Set();
  const items = Array.isArray(order.items) ? order.items : [];

  for (const item of items) {
    const product = item?.product || item?.productId;
    if (!product || typeof product !== 'object') continue;
    const creatorId = extractCreatorId(product.createdBy);
    if (!creatorId) continue;

    const teamUser = teamUserMap.get(String(creatorId));
    if (teamUser) {
      names.add(teamUser.name || teamUser.email || 'Team member');
      continue;
    }

    const fromProduct =
      getCreatorDisplayName(product.createdBy) ||
      (typeof product.createdBy === 'object'
        ? product.createdBy.name || product.createdBy.email
        : null);
    if (fromProduct) names.add(fromProduct);
  }

  if (names.size === 0) return '—';
  return [...names].join(', ');
}

function paymentSourceLabel(order, account) {
  if (isCodOrder(order)) return 'Cash on Delivery';
  if (account?.name) return account.name;
  const pm = String(order.paymentMethod || '').trim();
  return pm || 'Order payment';
}

export function orderHasCreatorProductLines(order, creatorId) {
  const id = String(creatorId || '').trim();
  if (!id || !order) return false;
  const items = Array.isArray(order.items) ? order.items : [];
  for (const item of items) {
    const product = item?.product || item?.productId;
    if (!product || typeof product !== 'object') continue;
    const cid = extractCreatorId(product.createdBy);
    if (cid && String(cid) === id) return true;
  }
  return false;
}

export function extractTransactionCreatorId(t) {
  if (!t) return null;
  const fromCreatedBy = extractCreatorId(t.createdBy);
  if (fromCreatedBy) return String(fromCreatedBy);
  for (const key of ['teamMemberId', 'soldBy', 'creatorId', 'userId']) {
    if (t[key]) return String(t[key]).trim();
  }
  if (t.teamMember && typeof t.teamMember === 'object') {
    const mid = t.teamMember._id || t.teamMember.id;
    if (mid) return String(mid);
  }
  return null;
}

/** Manual/API rows for one shopkeeper; order rows come from buildCreatorScopedOrderRevenueTransactions. */
export function filterApiTransactionsForCreator(apiRows, creatorId) {
  const id = String(creatorId || '').trim();
  if (!id) return [];
  return (apiRows || []).filter((t) => {
    if (t.isOrderSourced || extractOrderRef(t)) return false;
    const tid = extractTransactionCreatorId(t);
    return tid && tid === id;
  });
}

/** One income row per order = sum of that creator's line totals (not full order total). */
export function buildCreatorScopedOrderRevenueTransactions(
  orders,
  creatorId,
  accounts = [],
  teamUserMap = new Map()
) {
  const id = String(creatorId || '').trim();
  if (!id) return [];

  const report = buildSalesReport(orders, (cid) => cid && String(cid) === id);
  const orderById = new Map();
  for (const order of orders || []) {
    const oid = order._id || order.id;
    if (oid) orderById.set(String(oid), order);
  }

  const teamUser = teamUserMap.get(id);
  const teamLabel =
    teamUser?.name ||
    teamUser?.email ||
    getCreatorDisplayName(teamUser) ||
    'Team member';

  const list = [];

  for (const pay of report.payments) {
    const orderId = pay.orderId;
    const amount = toAmount(pay.myShare);
    if (!orderId || amount <= 0) continue;

    const order = orderById.get(String(orderId));
    const account = order ? resolvePaymentAccountFromOrder(order, accounts) : null;
    const status = order ? orderRevenueStatus(order) : 'completed';
    const cod = Boolean(pay.cod);
    const createdAt = pay.createdAt || new Date().toISOString();
    const orderNumber = pay.orderNumber || String(orderId).slice(-8);

    const accountId = account ? String(account._id || account.id || '').trim() : '';
    const accountName = account?.name || (cod ? 'Cash on Delivery' : paymentSourceLabel(order, account));

    list.push({
      _id: `order-revenue-${orderId}-creator-${id}`,
      id: `order-revenue-${orderId}-creator-${id}`,
      transactionId: `ORD-${orderNumber}`,
      type: 'income',
      source: accountName,
      amount,
      status,
      transactionDate: createdAt,
      createdAt,
      paymentAccount: account
        ? { _id: account._id || account.id, name: account.name, paymentMethod: account.paymentMethod }
        : cod
          ? { _id: 'cod', name: 'Cash on Delivery' }
          : null,
      accountId: accountId || (cod ? 'cod' : ''),
      accountName,
      isCOD: cod,
      isPending: status === 'pending',
      isOrderSourced: true,
      isCreatorScoped: true,
      creatorShare: amount,
      orderTotal: toAmount(pay.orderTotal),
      orderNumber,
      orderId: String(orderId),
      teamMember: teamLabel,
      orderStatus: order ? normalizeBackendOrderStatus(order) : undefined,
      paymentMethod: order?.paymentMethod,
    });
  }

  return list;
}

export function buildOrderRevenueTransactions(orders, accounts = [], teamUserMap = new Map()) {
  const list = [];

  for (const order of orders || []) {
    if (!order || !orderCountsTowardSoldSales(order)) continue;

    const orderId = order._id || order.id;
    if (!orderId) continue;

    const amount = toAmount(order.total);
    if (amount <= 0) continue;

    const account = resolvePaymentAccountFromOrder(order, accounts);
    const status = orderRevenueStatus(order);
    const cod = isCodOrder(order);
    const teamMember = getOrderTeamMemberLabel(order, teamUserMap);
    const createdAt = order.createdAt || order.date || new Date().toISOString();
    const orderNumber = order.orderNumber || String(orderId).slice(-8);

    const accountId = account ? String(account._id || account.id || '').trim() : '';
    const accountName = account?.name || (cod ? 'Cash on Delivery' : paymentSourceLabel(order, account));

    list.push({
      _id: `order-revenue-${orderId}`,
      id: `order-revenue-${orderId}`,
      transactionId: `ORD-${orderNumber}`,
      type: 'income',
      source: accountName,
      amount,
      status,
      transactionDate: createdAt,
      createdAt,
      paymentAccount: account
        ? { _id: account._id || account.id, name: account.name, paymentMethod: account.paymentMethod }
        : cod
          ? { _id: 'cod', name: 'Cash on Delivery' }
          : null,
      accountId: accountId || (cod ? 'cod' : ''),
      accountName,
      isCOD: cod,
      isPending: status === 'pending',
      isOrderSourced: true,
      orderNumber,
      orderId: String(orderId),
      teamMember,
      orderStatus: normalizeBackendOrderStatus(order),
      paymentMethod: order.paymentMethod,
    });
  }

  return list;
}

export function mergeRevenueTransactions(apiTransactions = [], orderTransactions = []) {
  const orderKeys = new Set();
  const merged = [];

  const pushRow = (row) => {
    const ref = extractOrderRef(row);
    if (ref) {
      if (orderKeys.has(ref)) return;
      orderKeys.add(ref);
    }
    merged.push(row);
  };

  // Sold-order rows first so API rows without orderId cannot duplicate the same sale.
  for (const row of orderTransactions) {
    pushRow(row);
  }

  for (const row of apiTransactions.map(normalizeApiTransaction)) {
    pushRow(row);
  }

  merged.sort(
    (a, b) =>
      new Date(b.transactionDate || b.createdAt || 0) -
      new Date(a.transactionDate || a.createdAt || 0)
  );

  return merged;
}

/** YYYY-MM-DD in local timezone (avoids toISOString UTC shift on presets). */
export function toLocalDateString(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getTransactionLocalDateString(t) {
  const raw = t?.transactionDate || t?.createdAt;
  if (!raw) return '';
  return toLocalDateString(new Date(raw));
}

/** Filter merged transactions for Revenue Breakdown date presets / custom range. */
export function filterTransactionsByDateRange(transactions = [], startDate = '', endDate = '') {
  const start = String(startDate || '').trim();
  const end = String(endDate || '').trim();
  if (!start && !end) return transactions;

  return transactions.filter((t) => {
    const local = getTransactionLocalDateString(t);
    if (!local) return false;
    if (start && local < start) return false;
    if (end && local > end) return false;
    return true;
  });
}

export function buildRevenueOverviewForRange(transactions = [], startDate = '', endDate = '') {
  const filtered = filterTransactionsByDateRange(transactions, startDate, endDate);
  return augmentOverviewFromTransactions(null, filtered);
}

/** Gross income for display (completed + pending, each order counted once). */
export function getRevenueGrossIncome(overview) {
  if (!overview) return 0;
  return toAmount(overview.totalIncome) + toAmount(overview.pendingIncome);
}

/** Single totals source — use everywhere in Revenue UI (no COD stacked twice). */
export function computeRevenueSummaryFromTransactions(transactions = []) {
  const completedIn = transactions.filter(
    (t) => (t.type === 'income' || t.type === 'deposit') && t.status === 'completed'
  );
  const pendingIn = transactions.filter(
    (t) => (t.type === 'income' || t.type === 'deposit') && t.status === 'pending'
  );
  const completedOut = transactions.filter(
    (t) => (t.type === 'withdrawal' || t.type === 'expense') && t.status === 'completed'
  );

  const sum = (arr) => arr.reduce((s, t) => s + toAmount(t.amount), 0);
  const codCompleted = completedIn.filter((t) => t.isCOD);
  const codPending = pendingIn.filter((t) => t.isCOD);

  const totalIncome = sum(completedIn);
  const pendingIncome = sum(pendingIn);
  const codRealIncome = sum(codCompleted);
  const codPendingIncome = sum(codPending);

  return {
    totalIncome,
    realIncome: totalIncome,
    pendingIncome,
    codRealIncome,
    codPendingIncome,
    codTotalIncome: codRealIncome + codPendingIncome,
    totalDeposits: totalIncome,
    totalWithdrawals: sum(completedOut),
    netRevenue: totalIncome - sum(completedOut),
    soldOrdersIncome: sum(completedIn.filter((t) => t.isOrderSourced)),
    /** Bank/digital accounts only (COD tracked separately on COD card). */
    nonCodCompletedIncome: sum(completedIn.filter((t) => !t.isCOD)),
  };
}

function mergeRevenueBySource(existing = [], fromTransactions = []) {
  const map = new Map();

  for (const item of existing || []) {
    const key = item._id || item.source || 'Other';
    map.set(key, {
      _id: key,
      total: toAmount(item.total),
      count: toAmount(item.count),
    });
  }

  for (const t of fromTransactions) {
    if (t.type !== 'income' && t.type !== 'deposit') continue;
    if (t.status !== 'completed') continue;
    const key = t.source || t.accountName || 'Other';
    const cur = map.get(key) || { _id: key, total: 0, count: 0 };
    cur.total += toAmount(t.amount);
    cur.count += 1;
    map.set(key, cur);
  }

  return [...map.values()].sort((a, b) => b.total - a.total);
}

/** Overview + COD fields from deduped transactions only (never stack API overview + orders twice). */
export function augmentOverviewFromTransactions(overview, transactions = []) {
  const summary = computeRevenueSummaryFromTransactions(transactions);
  return {
    ...(overview || {}),
    ...summary,
    revenueBySource: mergeRevenueBySource(
      [],
      transactions.filter((t) => t.status === 'completed')
    ),
  };
}

export async function fetchMergedRevenueTransactions(accounts = [], options = {}) {
  const creatorId = options.creatorId ? String(options.creatorId).trim() : '';

  let apiRows = [];
  try {
    const res = await revenueService.getTransactions();
    apiRows = normalizeApiTransactions(res).map(normalizeApiTransaction);
  } catch (e) {
    console.warn('fetchMergedRevenueTransactions: API revenue list', e);
  }

  let orders = [];
  let teamUserMap = new Map();
  try {
    [orders, teamUserMap] = await Promise.all([
      fetchAllAdminOrdersForAgg(),
      fetchTeamUserMap(),
    ]);
  } catch (e) {
    console.warn('fetchMergedRevenueTransactions: orders', e);
  }

  if (creatorId) {
    const apiForCreator = filterApiTransactionsForCreator(apiRows, creatorId);
    const orderRows = buildCreatorScopedOrderRevenueTransactions(
      orders,
      creatorId,
      accounts,
      teamUserMap
    );
    return mergeRevenueTransactions(apiForCreator, orderRows);
  }

  const orderRows = buildOrderRevenueTransactions(orders, accounts, teamUserMap);
  return mergeRevenueTransactions(apiRows, orderRows);
}
